import Link from 'components/Link'
import React from 'react'
import RightCol from '../RightCol'
import CallToAction from '../CallToAction'
import { Wrapper } from '../Wrapper'
import SearchIconButton from 'components/Search/SearchIconButton'
import { graphql, useStaticQuery } from 'gatsby'
import { GatsbyImage, getImage } from 'gatsby-plugin-image'

interface ColMenuItems {
    title: string
    description: string
    url: string
}

interface Tutorial {
    fields: {
        slug: string
    }
    frontmatter: {
        title: string
        featuredImage: any
    }
}

export default function UsingiCloudReady({ referenceElement }: { referenceElement: HTMLDivElement }) {
    const {
        tutorials: { nodes },
    } = useStaticQuery(query)

    const resources: ColMenuItems[] = [
        {
            title: 'Customer stories',
            description: 'See how iCloudReady is supporting industry leaders',
            url: '/customers',
        },
        {
            title: 'marketplace',
            description: 'Browse marketplace apps that help you get more out of iCloudReady',
            url: '/marketplace',
        },
        {
            title: 'Partner directory',
            description: 'Companies and products who can help with iCloudReady',
            url: '/partners',
        },
        {
            title: 'FAQ',
            description: 'Questions, but frequently asked',
            url: '/faq',
        },
        {
            title: 'Contact us',
            description: 'Licensing, upgrade, or demo inquiries',
            url: '/book-a-meeting#contact',
        },
        {
            title: 'iCloudReady on GitHub',
            description: 'See recent activity, browse libraries, request a feature, or file an issue',
            url: 'https://github.com/icloud-ready',
        },
    ]

    const userGuides: ColMenuItems[] = [
        {
            title: 'Property management',
            description: 'Insights, occupancy, rental accounting & reporting, maintenance + 5 more',
            url: '/start-icloudready#property-management',
        },
        {
            title: 'Visualize',
            description: 'Real estate crm, Heatmaps',
            url: '/start-icloudready#visualize',
        },
        {
            title: 'Optimize',
            description: 'Real estate marketing, websites & Websites + 1 more',
            url: '/start-icloudready#optimize',
        },
        {
            title: 'Data',
            description: 'Actions, cohorts, data management, sessions + 4 more',
            url: '/start-icloudready#data',
        },
        {
            title: 'Project settings',
            description: 'Team collaboration, organization and projects, toolbar + 3 more',
            url: '/start-icloudready#project-settings',
        },
    ]

    return (
        <Wrapper referenceElement={referenceElement} placement="bottom-start">
            <section className="flex flex-col-reverse md:flex-col">
                <div className="p-5 md:flex md:p-0">
                    <div className="border-dashed border-gray-accent-light md:w-[500px] md:border-r">
                        <div className="mb-4 md:mb-0 md:p-6">
                            <div className="flex w-full items-center justify-between opacity-70">
                                <h3 className="m-0 pl-2 text-[18px] font-bold text-black">Product manual</h3>
                                <SearchIconButton location="using-ph-dropdown" initialFilter="manual" />
                            </div>
                            <ol className="m-0 mt-2 list-none p-0">
                                {userGuides.map(({ title, description, url }: ColMenuItems, index) => {
                                    return (
                                        <li key={index}>
                                            <Link
                                                className="relative flex items-start space-x-2 rounded-md py-2 hover:bg-tan/50 active:top-[1px] active:scale-[.99] md:px-2"
                                                to={url}
                                            >
                                                <span className="mt-1 w-4 text-center text-[14px] font-semibold leading-none text-black/30 dark:text-white">
                                                    {index + 1}.
                                                </span>
                                                <div>
                                                    <h3 className="m-0 text-base opacity-70">{title}</h3>
                                                    <p className="m-0 text-sm font-medium  opacity-50 dark:text-white">
                                                        {description}
                                                    </p>
                                                </div>
                                            </Link>
                                        </li>
                                    )
                                })}
                            </ol>
                            <CallToAction className="mt-4 inline-block !w-full" to="/start-icloudready">
                                Visit product manual
                            </CallToAction>
                        </div>
                        <div className="mb-4 border-t border-b border-dashed border-gray-accent-light py-7 md:mb-0 md:border-b-0 md:px-6">
                            <div className="opacity-70">
                                <h3 className="m-0 text-[18px] font-bold  ">Latest tutorials</h3>
                            </div>
                            <ul className="m-0 mt-2 grid list-none grid-cols-1 gap-y-2 p-0">
                                {nodes.map(
                                    (
                                        { fields: { slug }, frontmatter: { title, featuredImage }, parent }: Tutorial,
                                        index
                                    ) => {
                                        const featuredImg = featuredImage || parent?.cover?.image
                                        const image = featuredImg && getImage(featuredImg)
                                        return (
                                            <li key={index}>
                                                <Link
                                                    className="m-0 inline-block text-sm font-semibold leading-tight text-red"
                                                    to={slug}
                                                >
                                                    {title}
                                                </Link>
                                            </li>
                                        )
                                    }
                                )}
                            </ul>
                            <CallToAction className="mt-4 inline-block !w-full" to="/tutorials">
                                Visit tutorials
                            </CallToAction>
                        </div>
                    </div>
                    <RightCol title="Resources">
                        <ol className="m-0 list-none p-0">
                            {resources.map(({ title, description, url }: ColMenuItems, index) => {
                                return (
                                    <li key={index}>
                                        <Link
                                            className="relative block rounded-sm py-2 px-3 hover:bg-tan/50 active:top-[1px] active:scale-[.99]"
                                            to={url}
                                        >
                                            <h3 className="m-0 text-base  opacity-70">{title}</h3>
                                            <p className="m-0 text-sm font-medium  opacity-50 dark:text-white">
                                                {description}
                                            </p>
                                        </Link>
                                    </li>
                                )
                            })}
                        </ol>
                    </RightCol>
                </div>
            </section>
        </Wrapper>
    )
}

const query = graphql`
    {
        tutorials: allMdx(
            filter: { fields: { slug: { regex: "/^/tutorials/" } } }
            limit: 3
            sort: { fields: frontmatter___date, order: DESC }
        ) {
            nodes {
                fields {
                    slug
                }
                frontmatter {
                    title
                    featuredImage {
                        childImageSharp {
                            gatsbyImageData(placeholder: NONE)
                        }
                    }
                }
                parent {
                    ... on GoogleDocs {
                        cover {
                            image {
                                childImageSharp {
                                    gatsbyImageData(width: 350)
                                }
                            }
                        }
                    }
                }
            }
        }
    }
`
